import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Goal, GoalType } from 'Generated/graphql-types'
import { useQuery } from '@apollo/client'
import { CampaignProgressChart } from '../../GoalPage/CampaignProgressChart/CampaignProgressChart'
import { ProgressChart } from '../../GoalPage/ProgressChart/ProgressChart'
import { ChartTypeOptionsEnum } from '../../GoalPage/types'
import { CHART_CATEGORY } from '../../../../../Apollo/Queries/ChartCategory'
import { CHART } from '../../../../../Apollo/Queries/Chart'

type ChartCardProps = {
  goal: Goal
  chartType?: ChartTypeOptionsEnum
}

export const ChartCard = ({
  goal,
  chartType = ChartTypeOptionsEnum.dot,
}: ChartCardProps) => {
  const [filteredChartData, setFilteredChartData] = useState<any>(null)

  const { data: chartData } = useQuery(CHART, {
    variables: { goalId: goal.id },
    skip: !goal.id,
  })

  const { data: chartCategoryData } = useQuery(CHART_CATEGORY, {
    variables: { chartId: goal.chartId },
    skip: !goal.chartId,
    fetchPolicy: 'cache-and-network', // Ensure we get fresh data after updates
  })

  // Filter out archived categories from the chart data
  useEffect(() => {
    if (chartData?.chart && chartCategoryData?.chartCategory) {
      // Get the list of active category IDs (not archived)
      const activeCategoryTitles = chartCategoryData.chartCategory
        .filter((category: any) => !category.archivedAt)
        .map((category: any) => category.title)

      // Filter the series data to only include active categories
      const filteredSeriesData = chartData.chart.seriesData.filter(
        (series: any) => activeCategoryTitles.includes(series.title)
      )

      setFilteredChartData({
        ...chartData.chart,
        seriesData: filteredSeriesData,
      })
    }
  }, [chartData, chartCategoryData])

  const funderSharedGoal = goal.FunderOrgToSharedGoal.filter(
    (sharedGoal) => sharedGoal.goalId === goal.id
  )[0]

  const goalIsSharedByFunder = funderSharedGoal?.isGoalSharedByFunder ?? false

  // Show loading state or empty state if we have no data yet
  if (!filteredChartData && chartData?.chart) {
    return <div>Loading chart data...</div>
  }

  return (
    <ChartContainer>
      {goal.type === GoalType.Campaign ? (
        <CampaignProgressChart goal={goal} />
      ) : (
        <ProgressChart
          goal={goal}
          isGoalSharedWithFunder={!!funderSharedGoal}
          goalIsSharedByFunder={goalIsSharedByFunder}
          chartType={chartType}
          simple
          customChartData={filteredChartData}
        />
      )}
    </ChartContainer>
  )
}

export const ChartContainer = styled.div`
  margin: 0;
`
